import React, { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const Demographics = ({ onNext, onBack, onDataChange, data }) => {
  const [basicInfo, setBasicInfo] = useState(data?.basicInfo || {
    name: '',
    age: '',
    gender: '',
    weight: '',
    medicalHistory: ''
  });

  const [contactInfo, setContactInfo] = useState(data?.contactInfo || {
    address: '123 Main St, Anytown, CA 90210',
    phone: '+1 (555) 123-4567',
    nextOfKin: 'Jane Doe (Sister) - +1 (555) 987-6543'
  });

  const [currentPage, setCurrentPage] = useState(1);

  const handleBasicChange = (field, value) => {
    const newInfo = { ...basicInfo, [field]: value };
    setBasicInfo(newInfo);
    if (onDataChange) {
      onDataChange({ demographics: { basicInfo: newInfo, contactInfo } });
    }
  };

  const handleContactChange = (field, value) => {
    const newInfo = { ...contactInfo, [field]: value };
    setContactInfo(newInfo);
    if (onDataChange) {
      onDataChange({ demographics: { basicInfo, contactInfo: newInfo } });
    }
  };

  const handleNextPage = () => {
    if (currentPage === 1) {
      setCurrentPage(2);
    } else {
      // Save all data before moving to next phase
      if (onDataChange) {
        onDataChange({ demographics: { basicInfo, contactInfo } });
      }
      onNext();
    }
  };

  const handlePrevPage = () => {
    if (currentPage === 2) {
      setCurrentPage(1);
    } else {
      onBack();
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-black py-4">
      <div className="w-full max-w-sm mx-auto px-4">
        <div className="flex flex-col min-h-screen bg-zinc-900 rounded-3xl border-2 border-orange-500/30 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b-2 border-orange-500/30">
            <Button
              onClick={handlePrevPage}
              variant="ghost"
              className="flex items-center gap-2 text-orange-500 text-lg font-bold"
            >
              <ArrowLeft className="w-6 h-6" />
              Back
            </Button>
            <h1 className="text-lg font-black text-orange-500">
              Demographics {currentPage}/2
            </h1>
            <div className="w-16" />
          </div>

          {/* Content */}
          <main className="flex-1 overflow-y-auto p-4 bg-black">
            {currentPage === 1 ? (
              <form className="space-y-5">
                <div>
                  <Label htmlFor="name" className="block text-lg font-bold text-orange-400 mb-2">
                    Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter patient's name"
                    value={basicInfo.name}
                    onChange={(e) => handleBasicChange('name', e.target.value)}
                    className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 placeholder-zinc-500 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <Label htmlFor="age" className="block text-lg font-bold text-orange-400 mb-2">
                    Age
                  </Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="Enter age"
                    value={basicInfo.age}
                    onChange={(e) => handleBasicChange('age', e.target.value)}
                    className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 placeholder-zinc-500 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <Label htmlFor="gender" className="block text-lg font-bold text-orange-400 mb-2">
                    Gender
                  </Label>
                  <select
                    id="gender"
                    value={basicInfo.gender}
                    onChange={(e) => handleBasicChange('gender', e.target.value)}
                    className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 focus:border-orange-500 focus:ring-orange-500"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="weight" className="block text-lg font-bold text-orange-400 mb-2">
                    Weight (kg)
                  </Label>
                  <Input
                    id="weight"
                    type="number"
                    placeholder="Enter weight"
                    value={basicInfo.weight}
                    onChange={(e) => handleBasicChange('weight', e.target.value)}
                    className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 placeholder-zinc-500 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <Label htmlFor="medical-history" className="block text-lg font-bold text-orange-400 mb-2">
                    Medical History
                  </Label>
                  <textarea
                    id="medical-history"
                    placeholder="Relevant medical history"
                    rows="3"
                    value={basicInfo.medicalHistory}
                    onChange={(e) => handleBasicChange('medicalHistory', e.target.value)}
                    className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 placeholder-zinc-500 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>
              </form>
            ) : (
              <form className="space-y-5">
                <div>
                  <Label htmlFor="address" className="block text-lg font-bold text-orange-400 mb-2">
                    Address
                  </Label>
                  <textarea
                    id="address"
                    placeholder="Enter patient's address"
                    rows="2"
                    value={contactInfo.address}
                    onChange={(e) => handleContactChange('address', e.target.value)}
                    className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 placeholder-zinc-500 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="block text-lg font-bold text-orange-400 mb-2">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Enter phone number"
                    value={contactInfo.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 placeholder-zinc-500 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <Label htmlFor="nextOfKin" className="block text-lg font-bold text-orange-400 mb-2">
                    Next of Kin
                  </Label>
                  <Input
                    id="nextOfKin"
                    type="text"
                    placeholder="Enter next of kin details"
                    value={contactInfo.nextOfKin}
                    onChange={(e) => handleContactChange('nextOfKin', e.target.value)}
                    className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100 placeholder-zinc-500 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>
              </form>
            )}
          </main>

          {/* Footer */}
          <div className="p-4 border-t-2 border-orange-500/30 bg-zinc-900">
            <Button
              onClick={handleNextPage}
              className="w-full h-14 bg-orange-500 text-white text-lg font-black rounded-xl hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 shadow-xl"
            >
              {currentPage === 1 ? 'Continue' : 'Next'}
              <ArrowRight className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Demographics;
